import { useContext, useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, List, ListItem, ButtonBar, useFlowFormGlobal } from 't-lib-ui/structure';
import { TextButton, Note, DropDownSelector, DropDownSelectorValue } from 't-lib-ui/widgets';
import { refreshIcon, trashIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { AppState } from '../../context/general';
import { nonNull } from 't-lib-ui/helpers';
import { adminListTags, adminListTagsApi, adminDeleteTagAuth, adminDeleteTagApi, adminGetApiServers } from '../../connector/api';
import { RestTagSummary } from '../../schema-copy/auth-schema';
import { RestApiServer } from '../../schema-copy/api-schema';

const AdminManageTagsPage: React.FC = () => {
  const appState = useContext(AppState);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [authFailed, setAuthFailed] = useState(0);
  const [tags, setTags] = useState<RestTagSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [confirmTag, setConfirmTag] = useState<string | undefined>(undefined);
  const [busyTag, setBusyTag] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  // Which API instance(s) to purge. Users are sharded per instance (EU users live only on e1), so
  // 'all' — the default — purges every instance before removing the auth accounts; picking a single
  // instance is an advanced override. Auth accounts (single-region) are always removed for the tag.
  const [apiServers, setApiServers] = useState<RestApiServer[]>([]);
  const [selectedServer, setSelectedServer] = useState('all');

  useFlowFormGlobal();

  const load = useCallback(async (secret: string) => {
    try {
      setLoading(true);
      setError(undefined);
      const auth = nonNull(appState.state.authData);
      // Tags are sharded across API instances (EU users live only on e1), so browse the tags of the
      // selected instance directly. 'all' falls back to the auth service, which spans every region.
      setTags(selectedServer == 'all'
        ? await adminListTags(auth, secret)
        : await adminListTagsApi(auth, secret, selectedServer));
    } catch (e: any) {
      if (e?.status == 401) setAuthFailed(n => n + 1);
      setError(e instanceof Error ? e.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [appState.state.authData, selectedServer]);

  useEffect(() => {
    if (adminSecret) load(adminSecret);
  }, [adminSecret, load]);

  // Load the configured API instances once the secret is verified (same list on every instance).
  useEffect(() => {
    if (!adminSecret) return;
    (async () => {
      try {
        setApiServers(await adminGetApiServers(nonNull(appState.state.authData), adminSecret));
      } catch (e) {
        console.error('Failed to load API servers:', e);
      }
    })();
  }, [adminSecret, appState.state.authData]);

  const serverOptions: DropDownSelectorValue[] = [
    { id: 'all', text: 'All servers (recommended)', searchTextLc: 'all servers recommended' },
    ...apiServers.map(s => ({ id: s.url, text: s.name, searchTextLc: s.name.toLowerCase() })),
  ];

  const handleDelete = async (tag: string) => {
    if (!adminSecret || busyTag) return;
    setBusyTag(tag);
    setError(undefined);
    setMessage(undefined);
    try {
      // Tags live in both services and the downread users are sharded per API instance. Purge the
      // users on every targeted instance FIRST, then the auth accounts (single-region) — same order
      // as a normal account deletion, and doing the auth delete last avoids orphaning any user whose
      // instance wasn't purged. 'all' covers the default host plus every configured instance so no
      // region is missed; a specific selection targets just that instance.
      const auth = nonNull(appState.state.authData);
      // undefined = the default host; dedup the explicit instance urls. A repeated purge of the same
      // DB simply removes 0 the second time, so overlap with the default host is harmless.
      const hosts: (string | undefined)[] = selectedServer == 'all'
        ? [undefined, ...Array.from(new Set(apiServers.map(s => s.url)))]
        : [selectedServer];

      let apiDeleted = 0;
      for (const host of hosts)
        apiDeleted += await adminDeleteTagApi(auth, tag, adminSecret, host);
      const authDeleted = await adminDeleteTagAuth(auth, tag, adminSecret);
      setMessage(`Deleted tag "${tag}": ${authDeleted} account${authDeleted == 1 ? '' : 's'} and ${apiDeleted} user${apiDeleted == 1 ? '' : 's'}.`);
      setConfirmTag(undefined);
      setTags(prev => prev.filter(t => t.tag != tag));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete tag');
    } finally {
      setBusyTag(undefined);
    }
  };

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} authFailed={authFailed} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
          <MainLayout
            selectedFeed="admin-users"
            right={({ onExpand }) => (
              <FlowForm main={true} className='full-margin'>
                <AdminHeader title="Manage Tags" onExpand={onExpand} />
                <ButtonBar size="small">
                  <TextButton
                    label="Refresh"
                    icon={refreshIcon}
                    onClick={() => adminSecret && load(adminSecret)}
                    focussable="flow"
                    busy={loading}
                  />
                </ButtonBar>

                {apiServers.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0' }}>
                    <label style={{ fontWeight: 600, color: 'var(--gray-350)', fontSize: '0.9rem' }}>
                      Delete on server
                    </label>
                    <DropDownSelector
                      selections={serverOptions}
                      value={selectedServer}
                      onSelect={(v: DropDownSelectorValue) => setSelectedServer(v.id as string)}
                      focussable="flow"
                    />
                  </div>
                )}

                <Note type='info'>
                  Tagged users group test/temporary accounts so they can be deleted in bulk here.
                  To create one, sign up on the normal sign-up page and append the tag to the email
                  address separated by a pipe — e.g. <strong>you@example.com|mytag3</strong>. The tag
                  must be 3–16 word characters (letters, digits or underscore). The account is created
                  with that tag (and is auto-confirmed), and the email part is used as the real address.
                  Deleting a tag here removes every account and user that carries it, in both the auth
                  and the Downread databases.
                </Note>

                {error && <Note type='error'>{error}</Note>}
                {message && <Note type='success'>{message}</Note>}

                <List>
                  {tags.map(t => (
                    <ListItem key={t.tag} focussable="flow">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{t.tag}</div>
                          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', marginTop: '4px' }}>
                            {t.count} {selectedServer == 'all' ? 'account' : 'user'}{t.count == 1 ? '' : 's'}
                          </div>
                        </div>
                        {confirmTag == t.tag ? (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ color: 'var(--error-color)', fontWeight: 600 }}>Delete all users of this tag?</span>
                            <TextButton
                              label="Yes, delete"
                              onClick={() => handleDelete(t.tag)}
                              focussable="flow"
                              type="major"
                              busy={busyTag == t.tag}
                              enabled={busyTag ? 'disabled' : 'enabled'}
                            />
                            <TextButton
                              label="Cancel"
                              onClick={() => setConfirmTag(undefined)}
                              focussable="flow"
                              enabled={busyTag ? 'disabled' : 'enabled'}
                            />
                          </div>
                        ) : (
                          <TextButton
                            label="Delete"
                            icon={trashIcon}
                            onClick={() => { setError(undefined); setMessage(undefined); setConfirmTag(t.tag); }}
                            focussable="flow"
                            enabled={busyTag ? 'disabled' : 'enabled'}
                          />
                        )}
                      </div>
                    </ListItem>
                  ))}
                  {!loading && !tags.length && !error && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                      No tagged users.
                    </div>
                  )}
                </List>
              </FlowForm>
            )}
            startWithLeftCollapsed={false}
          />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminManageTagsPage;
